import { useEffect, useState } from "react";
import { useViewer } from "../../../../state/ViewerProvider";
import type { InteractionPromptSettings } from "../../../../shared/api-contract";
import { CompactCheck, CompactNumber, CompactSlider, CompactText, Row } from "../../inspector/rows";
import type { InertTag } from "../../inspector/schema";

/**
 * Ten of the fourteen InteractionPromptSettings fields exist only in the
 * overlay's defaults literal. The overlay reads `enabled`, `text`, `showOnLoad`
 * and `idleDelay` and nothing else: it is a CSS transition, not an animated
 * hand, and its only auto-show paths are showOnLoad and the idle timer.
 */
const PROMPT_ANIM_INERT: InertTag = {
  reason: "Stored, never read. The overlay is a CSS fade \u2014 it has no animation of its own to time or offset.",
};
const PROMPT_AUTO_INERT: InertTag = {
  reason: "Stored, never read. The overlay's only auto-show paths are \u201cShow on load\u201d and \u201cIdle delay\u201d above.",
};

const DEFAULT: InteractionPromptSettings = {
  enabled: false, text: "Drag to rotate", showOnLoad: true, idleDelay: 12,
  animationDuration: 2000, animationDistance: 80, animationPauseDuration: 6000,
  rotationDistance: 0.3, yOffset: 0, autoStart: true, autoStartDelay: 30000,
  autoStop: true, autoStartOnObjectLoad: true, autoStartOnObjectLoadDelay: 3000,
};

/**
 * Viewer-side "drag to rotate" hint (v15). The overlay lives in the iframe;
 * this panel just edits its settings. One-shot fetch, patch-returning setter.
 */
export function InteractionPromptControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [s, setS] = useState<InteractionPromptSettings>(DEFAULT);

  useEffect(() => { r.getInteractionPrompt().then(setS).catch(() => {}); }, [r]);
  const patch = (p: Partial<InteractionPromptSettings>) => void r.setInteractionPrompt(p).then(setS);

  return (
    <div className="flex flex-col gap-1.5 px-3 py-1.5">
      <label className="flex items-center justify-between gap-2 text-[12px] text-ed-text">
        Show prompt
        <CompactCheck checked={s.enabled} onChange={(v) => patch({ enabled: v })} />
      </label>
      <div className="flex items-center gap-2">
        <span className="w-16 shrink-0 text-[11px] text-ed-text-3">Text</span>
        <CompactText value={s.text} placeholder="Drag to rotate" onChange={(v) => patch({ text: v })} />
      </div>
      <label className="flex items-center justify-between gap-2 text-[12px] text-ed-text">
        Show on load
        <CompactCheck checked={s.showOnLoad} onChange={(v) => patch({ showOnLoad: v })} />
      </label>
      <div className="flex items-center gap-2">
        <span className="w-16 shrink-0 text-[11px] text-ed-text-3">Idle delay</span>
        <CompactSlider value={s.idleDelay} min={0} max={60} step={1} onChange={(v) => patch({ idleDelay: v })} format={(v) => (v <= 0 ? "off" : `${v}s`)} />
      </div>
      <Row label="Anim Duration (ms)" inert={PROMPT_ANIM_INERT}><CompactNumber value={s.animationDuration} width={72} onChange={(v) => patch({ animationDuration: v })} /></Row>
      <Row label="Anim Distance (px)" inert={PROMPT_ANIM_INERT}><CompactNumber value={s.animationDistance} width={72} onChange={(v) => patch({ animationDistance: v })} /></Row>
      <Row label="Pause Duration (ms)" inert={PROMPT_ANIM_INERT}><CompactNumber value={s.animationPauseDuration} width={72} onChange={(v) => patch({ animationPauseDuration: v })} /></Row>
      <Row label="Rotation Distance" inert={PROMPT_ANIM_INERT}><CompactNumber value={s.rotationDistance} step={0.01} width={72} onChange={(v) => patch({ rotationDistance: v })} /></Row>
      <Row label="Y Offset" inert={PROMPT_ANIM_INERT}><CompactNumber value={s.yOffset} width={72} onChange={(v) => patch({ yOffset: v })} /></Row>
      <Row label="Auto Start" inert={PROMPT_AUTO_INERT}><CompactCheck checked={s.autoStart} onChange={(v) => patch({ autoStart: v })} /></Row>
      <Row label="Auto Start Delay (ms)" inert={PROMPT_AUTO_INERT}><CompactNumber value={s.autoStartDelay} width={72} onChange={(v) => patch({ autoStartDelay: v })} /></Row>
      <Row label="Auto Stop" inert={PROMPT_AUTO_INERT}><CompactCheck checked={s.autoStop} onChange={(v) => patch({ autoStop: v })} /></Row>
    </div>
  );
}
